/** Review workbench — every live review cycle, with the refusal stated up front.
 *
 * The point of this screen is that NOBODY has to press a button to find out why a
 * close-out will not go through. The server computes the same readiness the close-out
 * dialog computes and returns it on every row, so the queue and the dialog cannot
 * disagree, and the 409 is printed on the row before anyone tries.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme, day } from '@/components/nfa/nfaTheme';
import { Btn, Empty, Field, Panel, Select, Stat } from '@/components/nfa/NfaPrimitives';

const D = {
  open: 'A review cycle that is neither closed out nor cancelled.',
  overdue: 'Today is past the cycle\u2019s due date. Counted per cycle, never per reviewer — '
    + 'three silent reviewers on one cycle are one overdue review, not three.',
  blocked: 'The close-out would be refused with 409 because the revision carries comments '
    + 'nobody has dispositioned. The comments are listed on the row, so the blockage is '
    + 'actionable rather than mysterious.',
  closeable: 'Stage allows close-out AND every comment is dispositioned. Whether YOU may '
    + 'close it out depends on your document-control role, stated on each row.',
  mine: 'Cycles where you are named as a reviewer.',
};

const EdmReviewWorkbench = ({ projectId }) => {
  const T = useNfaTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState('');
  const [mine, setMine] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!projectId) return;
    const qs = new URLSearchParams();
    if (stage) qs.set('stage', stage);
    if (mine) qs.set('mine', 'true');
    if (blockedOnly) qs.set('blocked_only', 'true');
    api.get(`/edm/projects/${projectId}/review-workbench?${qs}`)
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], stats: {} }));
  }, [projectId, stage, mine, blockedOnly]);
  useEffect(load, [load]);

  const move = async (row, toStage) => {
    setBusy(row.cycle_id);
    try {
      await api.post(`/edm/review-cycles/${row.cycle_id}/transition`, { to_stage: toStage });
      toast.success(`${row.document_no} ${row.revision_code} → ${toStage.replace(/_/g, ' ')}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.error || 'The stage move was refused'));
    } finally { setBusy(null); }
  };

  const s = data?.stats || {};
  const rows = data?.rows || [];
  return (
    <div className="space-y-3" data-testid="edm-review-workbench">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat T={T} label="Open cycles" value={s.open ?? '—'} info={D.open}
          testId="edm-rw-stat-open" />
        <Stat T={T} label="Overdue" value={s.overdue ?? '—'} info={D.overdue}
          tone={s.overdue ? T.red : undefined}
          sub={s.worstDaysOverdue ? `worst ${s.worstDaysOverdue}d` : undefined}
          testId="edm-rw-stat-overdue" />
        <Stat T={T} label="Blocked by comments" value={s.blockedByComments ?? '—'}
          info={D.blocked} tone={s.blockedByComments ? T.amber : undefined}
          sub="would refuse with 409" testId="edm-rw-stat-blocked" />
        <Stat T={T} label="Closeable now" value={s.closeableNow ?? '—'} info={D.closeable}
          tone={s.closeableNow ? T.green : undefined} testId="edm-rw-stat-closeable" />
        <Stat T={T} label="Mine" value={s.mine ?? '—'} info={D.mine}
          testId="edm-rw-stat-mine" />
      </div>

      <Panel T={T} testId="edm-review-queue" pad={false} title="Review workbench"
        subtitle={data?.note}
        right={<>
          <Field T={T} label="Stage">
            <Select T={T} value={stage} onChange={(e) => setStage(e.target.value)}
              data-testid="edm-rw-stage">
              <option value="">Every live stage</option>
              {(data?.stages || []).map((x) => (
                <option key={x.stage} value={x.stage}>{x.label}</option>
              ))}
            </Select>
          </Field>
          <Btn T={T} variant={mine ? 'primary' : 'ghost'} onClick={() => setMine((v) => !v)}
            testId="edm-rw-mine">Only mine</Btn>
          <Btn T={T} variant={blockedOnly ? 'warn' : 'ghost'}
            onClick={() => setBlockedOnly((v) => !v)} testId="edm-rw-blocked">
            Only blocked</Btn>
        </>}>
        {!data && <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</Empty>}
        {data && rows.length === 0 && (
          <Empty T={T} testId="edm-rw-empty">
            No live review cycle matches. A cycle appears here the moment one is opened on a
            revision, and leaves it when it is closed out or cancelled.
          </Empty>
        )}
        {rows.map((r) => (
          <div key={r.cycle_id} data-testid={`edm-rw-row-${r.cycle_id}`}
            className="px-4 py-3 text-[11.5px]"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="min-w-0">
                <button type="button" className="font-mono text-[11.5px] hover:underline"
                  style={{ color: T.primary }}
                  data-testid={`edm-rw-open-${r.cycle_id}`}
                  onClick={() => navigate(`/engineering/revisions/${r.revision_id}/viewer`)}>
                  {r.document_no} · {r.revision_code}
                </button>
                <div className="truncate max-w-[320px]" style={{ color: T.ink }}>{r.title}</div>
                <div className="text-[9.5px]" style={{ color: T.muted }}>
                  {r.discipline || 'no discipline'} · cycle {r.cycle_no} · {r.purpose || 'review'}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: T.body }}>{r.stageLabel}</div>
                <div className="text-[9.5px] flex items-center gap-1 mt-0.5"
                  style={{ color: r.overdue ? T.red : T.muted }}
                  data-testid={`edm-rw-due-${r.cycle_id}`}>
                  <Clock className="w-3 h-3" />
                  {r.due_date
                    ? (r.overdue
                      ? `${r.daysOverdue}d overdue · due ${day(r.due_date)}`
                      : `due ${day(r.due_date)} · ${r.daysRemaining}d left`)
                    : 'no due date set'}
                </div>
                {r.awaiting?.length > 0 && (
                  <div className="text-[9.5px] mt-0.5" style={{ color: T.muted }}>
                    waiting on {r.awaiting.join(', ')}
                  </div>
                )}
              </div>

              <div className="ml-auto text-right">
                {r.closeOut.ok ? (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold"
                    style={{ color: T.green }} data-testid={`edm-rw-ready-${r.cycle_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> ready to close out
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold"
                    style={{ color: T.amber }} data-testid={`edm-rw-gate-${r.cycle_id}`}>
                    <AlertTriangle className="w-3.5 h-3.5" /> {r.closeOut.wouldRefuseWith}
                  </span>
                )}
                <div className="text-[9.5px] mt-0.5 max-w-[340px]" style={{ color: T.muted }}>
                  {r.closeOut.blockReason}
                </div>
                {!r.closeOut.youMayCloseOut && (
                  <div className="text-[9.5px] mt-0.5 inline-flex items-center gap-1"
                    style={{ color: T.muted }}
                    data-testid={`edm-rw-authority-${r.cycle_id}`}>
                    <ShieldAlert className="w-3 h-3" /> {r.closeOut.authorityReason}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-1.5 justify-end flex-wrap">
                  {(r.nextStages || []).filter((x) => x.stage !== 'CLOSED_OUT').map((x) => (
                    <Btn key={x.stage} T={T} variant="ghost" disabled={busy === r.cycle_id}
                      onClick={() => move(r, x.stage)}
                      testId={`edm-rw-move-${r.cycle_id}-${x.stage}`}>
                      {busy === r.cycle_id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      {x.stage.replace(/_/g, ' ').toLowerCase()}
                    </Btn>
                  ))}
                </div>
              </div>
            </div>

            {(r.closeOut.open || []).length > 0 && (
              <div className="mt-2 rounded-md px-2.5 py-2"
                data-testid={`edm-rw-open-comments-${r.cycle_id}`}
                style={{ background: T.tint(T.amber), border: `1px solid ${T.tint(T.amber, '44')}` }}>
                <div className="text-[10px] font-semibold" style={{ color: T.ink }}>
                  {r.openComments} comment(s) undispositioned
                  {r.holdComments ? `, ${r.holdComments} on hold` : ''} — this is what the 409 is
                </div>
                <ul className="mt-1 space-y-0.5">
                  {r.closeOut.open.slice(0, 6).map((c, i) => (
                    <li key={c.comment_id || i} className="text-[10px]" style={{ color: T.body }}>
                      · {c.severity ? `[${c.severity}] ` : ''}{c.body || c.summary || c.comment_id}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
};

export default EdmReviewWorkbench;
